import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  LinearProgress,
} from '@mui/material';
import ProjectBuilderDashboard from '../components/ProjectBuilder/ProjectBuilderDashboard';
import PhaseNavigationToolbar from '../components/ProjectBuilder/PhaseNavigationToolbar';
import PhaseList from '../components/ProjectBuilder/PhaseList';
import PhaseConfigurationPanel from '../components/ProjectBuilder/PhaseConfigurationPanel';
import TransitionLogicEditor from '../components/ProjectBuilder/TransitionLogicEditor';
import ProjectDefinitionViewer from '../components/ProjectBuilder/ProjectDefinitionViewer';
import CreateProjectDialog from '../components/ProjectBuilder/CreateProjectDialog';
import ExcelImportWizard from '../components/ProjectBuilder/ExcelImportWizard';
import SaveTemplateDialog from '../components/Template/SaveTemplateDialog';
import ProjectUserManagement from '../components/Project/ProjectUserManagement';
import { ProjectDefinition, PhaseDefinition, TransitionDefinition, ProjectPlan, PlanningPhase, PlanningTask, PlanningAction } from '../types';
import { detectCircularDependencies, formatCyclePath, validateTransitions } from '../utils/circularDependencyDetection';
import {
  getProjectDefinition,
  createProjectDefinition,
  updateProjectDefinition,
  ProjectDefinitionResponse,
} from '../services/projectDefinitionsApi';
import { createProject, listProjects, downloadBlankExcelTemplate, ProjectApiError } from '../services/projectApi';
import {
  createPhase,
  createTask,
  createAction,
  PlanningExecutionApiError,
} from '../services/planningExecutionApi';
import { useAuth } from '../contexts/AuthContext';

type ViewMode = 'dashboard' | 'builder' | 'preview';

interface ValidationError {
  field: string;
  message: string;
}

const ProjectBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const { projectId, mode } = useParams<{ projectId?: string; mode?: string }>();
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  const [currentProject, setCurrentProject] = useState<ProjectDefinition | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [currentView, setCurrentView] = useState<ViewMode>('dashboard');
  const [dashboardKey, setDashboardKey] = useState(0); // Key to force dashboard refresh
  const [editingPhase, setEditingPhase] = useState<PhaseDefinition | null>(null);
  const [showPhaseForm, setShowPhaseForm] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  const [showCreateProjectDialog, setShowCreateProjectDialog] = useState(false);
  const [excelImportWizardOpen, setExcelImportWizardOpen] = useState(false);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [associatedProjectId, setAssociatedProjectId] = useState<number | null>(null);
  
  // Helper function to retry API calls with authentication error handling
  const retryApiCall = async <T,>(
    apiCall: () => Promise<T>,
    operationName: string,
    retryCount = 0
  ): Promise<T> => {
    // Don't attempt if not authenticated
    if (!isAuthenticated) {
      throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
    }

    try {
      return await apiCall();
    } catch (err) {
      const apiError = err as ProjectApiError | PlanningExecutionApiError;
      
      // Handle authentication errors with retry logic
      if (apiError.code === 'AUTH_REQUIRED') {
        // If auth is required but we think we're authenticated, wait and retry
        // This handles the race condition where cookies aren't set yet
        if (retryCount < 2 && isAuthenticated) {
          const delay = Math.pow(2, retryCount) * 500; // 500ms, 1000ms
          console.warn(`${operationName}: Auth not ready, retrying in ${delay}ms (attempt ${retryCount + 1}/2)`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return retryApiCall(apiCall, operationName, retryCount + 1);
        } else {
          throw { error: true, message: 'Authentication required. Please refresh the page and try again.', code: 'AUTH_REQUIRED' } as ProjectApiError;
        }
      } else {
        // For other errors, don't retry - just throw
        throw err;
      }
    }
  };
  
  // Convert API response to frontend format
  const convertApiToFrontend = useCallback((apiResponse: ProjectDefinitionResponse): ProjectDefinition => {
    // Convert phases: API uses phaseDefinitionId, frontend uses phaseId
    const phases: PhaseDefinition[] = apiResponse.phases.map(phase => ({
      phaseId: phase.phaseDefinitionId,
      name: phase.name,
      description: phase.description || undefined,
      entryCriteria: phase.entryCriteria || undefined,
      exitActions: phase.exitActions.map(ea => ea.exitAction),
      displayOrder: phase.displayOrder,
    }));
    
    // Convert transitions: API uses fromPhaseDefinitionId/toPhaseDefinitionId, frontend uses fromPhaseId/toPhaseId
    const transitions: TransitionDefinition[] = [];
    apiResponse.phases.forEach(phase => {
      phase.transitions.forEach(transition => {
        transitions.push({
          transitionId: transition.transitionId,
          fromPhaseId: transition.fromPhaseDefinitionId,
          toPhaseId: transition.toPhaseDefinitionId,
          condition: transition.condition || undefined,
        });
      });
    });
    
    return {
      projectId: apiResponse.projectDefinitionId, // Use projectDefinitionId as projectId
      projectName: apiResponse.name,
      createdDate: apiResponse.createdAt,
      lastUpdated: apiResponse.lastUpdated,
      phases,
      transitions,
    };
  }, []);
  
  // Convert frontend format to API request format
  const convertFrontendToApi = useCallback((frontend: ProjectDefinition) => {
    // Convert phases with transitions and exit actions
    const phases = frontend.phases.map(phase => {
      // Find transitions that start from this phase
      const phaseTransitions = frontend.transitions
        .filter(t => t.fromPhaseId === phase.phaseId)
        .map(t => ({
          toPhaseDefinitionId: t.toPhaseId,
          condition: t.condition || '',
        }));
      
      // Convert exit actions
      const exitActions = phase.exitActions.map((action, index) => ({
        exitAction: action,
        target: null, // Frontend doesn't have target field
        displayOrder: index,
      }));
      
      return {
        name: phase.name,
        description: phase.description || null,
        entryCriteria: phase.entryCriteria || null,
        displayOrder: phase.displayOrder,
        transitions: phaseTransitions,
        exitActions,
      };
    });
    
    return {
      name: frontend.projectName,
      description: null, // Not in database schema
      projectId: null, // Not in database schema
      phases,
    };
  }, []);

  // Determine view from URL params
  useEffect(() => {
    if (!projectId) {
      setCurrentView('dashboard');
      // Increment dashboard key to force refresh when returning to dashboard
      setDashboardKey(prev => prev + 1);
    } else if (mode === 'preview') {
      setCurrentView('preview');
      loadProject(projectId);
    } else {
      setCurrentView('builder');
      loadProject(projectId);
    }
  }, [projectId, mode]);

  const loadProject = useCallback(async (id: string) => {
    setIsLoading(true);
    try {
      // Parse as number (API ID) - database is the single source of truth
      const numericId = parseInt(id, 10);
      if (isNaN(numericId) || numericId <= 0) {
        throw new Error(`Invalid project ID: ${id}. Project IDs must be positive numbers.`);
      }
      
      // Load from API
      const apiResponse = await getProjectDefinition(numericId);
      const project = convertApiToFrontend(apiResponse);
      setCurrentProject(project);
      setIsDirty(false);
      setValidationErrors([]);
      
      // Find associated Project for "Save as Template" functionality
      try {
        const projectsResponse = await listProjects({
          projectDefinitionId: numericId,
          page: 1,
          pageSize: 1,
        });
        if (projectsResponse.projects && projectsResponse.projects.length > 0) {
          setAssociatedProjectId(projectsResponse.projects[0].projectId);
        } else {
          setAssociatedProjectId(null);
        }
      } catch (err) {
        console.warn('Could not find associated project for template saving:', err);
        setAssociatedProjectId(null);
      }
    } catch (error) {
      // Handle API errors
      let errorMessage = 'Failed to load project';
      
      if (error && typeof error === 'object' && 'code' in error) {
        if (error.code === 'AUTH_REQUIRED') {
          errorMessage = 'Authentication required. Please log in.';
        } else if (error.code === 'NOT_FOUND') {
          errorMessage = `Project ${id} not found`;
        } else if ('message' in error) {
          errorMessage = (error as { message: string }).message;
        }
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
      navigate('/builder');
      console.error('Error loading project:', error);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, convertApiToFrontend]);

  const handleCreateNew = () => {
    // Open dialog to collect project info
    setShowCreateProjectDialog(true);
  };

  const handleCreateProject = async (projectData: { name: string; projectStartDate: string; airportId: number }) => {
    // Check authentication before starting - but allow if auth is loading (will retry)
    if (!isAuthenticated && !authLoading) {
      const error = { error: true, message: 'Authentication required. Please refresh the page and try again.', code: 'AUTH_REQUIRED' } as ProjectApiError;
      setSnackbar({
        open: true,
        message: 'Authentication required. Please refresh the page and try again.',
        severity: 'error',
      });
      throw error;
    }
    
    // If auth is still loading, wait a bit and check again
    if (authLoading) {
      // Wait for auth to complete (max 3 seconds)
      let retries = 0;
      const maxRetries = 6; // 6 * 500ms = 3 seconds max wait
      while (authLoading && retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 500));
        retries++;
      }
      
      // After waiting, check again
      if (!isAuthenticated) {
        const error = { error: true, message: 'Authentication required. Please refresh the page and try again.', code: 'AUTH_REQUIRED' } as ProjectApiError;
        setSnackbar({
          open: true,
          message: 'Authentication required. Please refresh the page and try again.',
          severity: 'error',
        });
        throw error;
      }
    }

    try {
      // Step 1: Create the ProjectDefinition with 1 initial phase
      const initialPhase: PhaseDefinition = {
        phaseId: -1, // Temporary ID, will be replaced by server
        name: 'Project Start',
        description: 'Initial project phase',
        entryCriteria: '',
        exitActions: [],
        displayOrder: 1, // 1-based indexing
      };

      const newDefinition: ProjectDefinition = {
        projectId: -1, // Temporary ID
        projectName: projectData.name,
        createdDate: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        phases: [initialPhase],
        transitions: [],
      };
      
      // Save the definition with retry logic
      const apiData = convertFrontendToApi(newDefinition);
      const savedDefinition = await retryApiCall(
        () => createProjectDefinition(apiData),
        'Create Project Definition'
      );
      
      // Step 2: Create the Project with the projectDefinitionId already linked
      // CRITICAL: Log the exact date value being sent to API - NO conversion
      console.log('ProjectBuilderPage: Creating project with date:',
        'projectData.projectStartDate=' + JSON.stringify(projectData.projectStartDate),
        'type=' + typeof projectData.projectStartDate,
        'NATIVE VALUE - NO CONVERSION'
      );
      
      const createProjectRequest = {
        name: projectData.name,
        projectStartDate: projectData.projectStartDate, // Use directly - NO conversion
        projectDefinitionId: savedDefinition.projectDefinitionId,
        airportId: projectData.airportId, // Required - already validated in dialog
      };
      
      console.log('ProjectBuilderPage: createProjectRequest.projectStartDate=',
        JSON.stringify(createProjectRequest.projectStartDate)
      );
      
      const createdProject = await retryApiCall(
        () => createProject(createProjectRequest),
        'Create Project'
      );
      
      // Step 3: Create the initial phase, task, and action in the planning API
      {
        // Find the phase ID from the saved definition (should be the first phase)
        const savedPhase = savedDefinition.phases && savedDefinition.phases.length > 0 
          ? savedDefinition.phases[0] 
          : null;
        
        if (savedPhase) {
          // Create the planning phase with retry logic
          const planningPhase = await retryApiCall(
            () => createPhase(createdProject.projectId, {
              name: 'Project Start', // Use consistent naming
              description: savedPhase.description || 'Initial project phase',
              displayOrder: 1, // 1-based indexing
              plannedStartDate: null,
              plannedEndDate: null,
              plannedDurationHours: 8, // Default 8 hours
            }),
            'Create Planning Phase'
          );

          // Create the initial task with retry logic
          const planningTask = await retryApiCall(
            () => createTask(planningPhase.phaseId, {
              name: 'Project Kickoff',
              description: 'Project kickoff task',
              displayOrder: 1, // 1-based indexing
              plannedStartDate: null,
              plannedEndDate: null,
              plannedDurationHours: 8, // Default 8 hours
              prerequisiteTaskIds: [], // Backend expects prerequisiteTaskIds (not prerequisites)
              parallelExecution: false,
            }),
            'Create Planning Task'
          );

          // Create the initial action with retry logic
          await retryApiCall(
            () => createAction(planningTask.taskId, {
              actionType: 'CREATE',
              durationHours: 8, // Backend requires this field name (required)
              notes: 'Create Project Scope Doc',
              displayOrder: 1, // 1-based indexing
              plannedStartDate: null,
              plannedEndDate: null,
              plannedDurationHours: 8,
            }),
            'Create Planning Action'
          );
        }
      }
      
      // Navigate to the Planning page for editing (this is the full Project Edit UI)
      navigate(`/planning?projectId=${createdProject.projectId}`);
    } catch (error) {
      const errorMessage = (error && typeof error === 'object' && 'message' in error)
        ? (error as { message: string }).message
        : 'Failed to create project';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
      throw error; // Re-throw so dialog can handle it
    }
  };

  const handleViewProject = async (projectDefinitionId: number) => {
    try {
      // Check if a Project exists for this ProjectDefinition
      // Note: Backend dev - does listProjects support filtering by projectDefinitionId?
      // If not, we may need to fetch all projects and filter client-side, or add this filter
      const projectsResponse = await listProjects({ 
        projectDefinitionId,
        page: 1,
        pageSize: 10,
      });

      // Find project that matches this projectDefinitionId
      const matchingProject = projectsResponse.projects?.find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (p: any) => p.projectDefinitionId === projectDefinitionId
      );

      if (matchingProject) {
        // Project exists - check status
        // If status is PLANNING (pending/not started), open Planning page for editing
        // If status is EXECUTION or beyond, open execution/tracking page
        if (matchingProject.status === 'PLANNING') {
          // Project exists but is in planning - open Planning page (full Project Edit UI)
          navigate(`/planning?projectId=${matchingProject.projectId}`);
        } else {
          // Project has begun (EXECUTION, COMPLETE, etc.) - open execution/tracking page
          navigate(`/execution?projectId=${matchingProject.projectId}`);
        }
      } else {
        // No Project exists yet - this shouldn't happen if projectDefinitionId is linked
        // But if it does, we can't open planning without a projectId, so show error
        setSnackbar({
          open: true,
          message: 'Project instance not found. Please create a project first.',
          severity: 'error',
        });
      }
    } catch (error) {
      // If API call fails, default to opening builder
      console.error('Error checking project status:', error);
      navigate(`/builder/${projectDefinitionId}`);
    }
  };

  const handleSelectProject = (id: string | number) => {
    const idString = id.toString();
    if (currentView === 'dashboard') {
      // From dashboard, go to builder view
      navigate(`/builder/${idString}`);
    } else {
      // Already in a project view, just load it
      loadProject(idString);
    }
  };

  const convertDefinitionToPlan = (definition: ProjectDefinition): ProjectPlan => {
    // Convert ProjectDefinition to ProjectPlan format
    // Since ProjectDefinition doesn't have tasks/actions, we create a basic structure
    const phases: PlanningPhase[] = definition.phases.map((phase, _phaseIndex) => {
      // Create a default action for the task (required by validation)
      const defaultAction: PlanningAction = {
        actionId: phase.phaseId * 1000 + 1, // Generate unique numeric ID
        actionType: 'CREATE',
        duration: 8, // Default 8 hours (1 working day)
        assignedTo: [],
        notes: '',
        displayOrder: 1,
      };

      // Create a default task for each phase (user can add more in planning UI)
      const defaultTask: PlanningTask = {
        taskId: phase.phaseId * 100 + 1, // Generate unique numeric ID
        name: `${phase.name} - Initial Task`,
        description: phase.description || '',
        assignedUsers: [],
        startDate: null,
        endDate: null,
        duration: 8, // Default 8 hours (1 working day) - matches action duration
        prerequisites: [],
        parallelExecution: false,
        actions: [defaultAction], // At least one action required
        displayOrder: 1,
      };

      return {
        phaseId: phase.phaseId,
        name: phase.name,
        description: phase.description || '',
        startDate: null,
        endDate: null,
        duration: 8, // Default 8 hours - will be calculated from tasks
        tasks: [defaultTask],
        displayOrder: phase.displayOrder,
      };
    });

    return {
      projectId: definition.projectId,
      projectName: definition.projectName,
      projectStartDate: definition.createdDate.split('T')[0], // Convert ISO datetime to date string
      phases,
    };
  };

  // @ts-ignore - intentionally unused
  const _handleOpenPlanning = async (projectId: string) => {
    try {
      let definition: ProjectDefinition | null = null;
      
      // If we have the current project and it matches, use it
      if (currentProject && currentProject.projectId.toString() === projectId) {
        definition = currentProject;
      } else {
        // Load from API - database is the single source of truth
        const numericId = parseInt(projectId, 10);
        if (isNaN(numericId) || numericId <= 0) {
          throw new Error(`Invalid project ID: ${projectId}. Project IDs must be positive numbers.`);
        }
        
        const apiResponse = await getProjectDefinition(numericId);
        definition = convertApiToFrontend(apiResponse);
      }
      
      if (definition) {
        // Convert ProjectDefinition to ProjectPlan (conversion done but plan not used - navigation uses projectId directly)
        convertDefinitionToPlan(definition);
        // Navigate to planning page with projectId
        navigate(`/planning?projectId=${definition.projectId}`);
      } else {
        setSnackbar({
          open: true,
          message: `Project ${projectId} not found`,
          severity: 'error',
        });
      }
    } catch (error) {
      let errorMessage = 'Failed to open planning';
      if (error && typeof error === 'object' && 'code' in error) {
        if (error.code === 'AUTH_REQUIRED') {
          errorMessage = 'Authentication required. Please log in.';
        } else if (error.code === 'NOT_FOUND') {
          errorMessage = `Project ${projectId} not found`;
        } else if ('message' in error) {
          errorMessage = (error as { message: string }).message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
      console.error('Error opening planning:', error);
    }
  };

  const handleSave = useCallback(async () => {
    if (!currentProject) return;

    // Validate before saving
    const errors = validateProject(currentProject);
    if (errors.length > 0) {
      setValidationErrors(errors);
      setSnackbar({
        open: true,
        message: 'Please fix validation errors before saving',
        severity: 'error',
      });
      return;
    }

    setIsSaving(true);
    setIsSaved(false);
    
    try {
      const apiData = convertFrontendToApi(currentProject);
      let savedProject: ProjectDefinition;
      
      // Check if this is a new project (negative ID) or existing (positive ID)
      if (currentProject.projectId < 0) {
        // Create new project
        const apiResponse = await createProjectDefinition(apiData);
        savedProject = convertApiToFrontend(apiResponse);
        // Update URL with new ID
        navigate(`/builder/${savedProject.projectId}`, { replace: true });
      } else {
        // Update existing project
        const apiResponse = await updateProjectDefinition(currentProject.projectId, apiData);
        savedProject = convertApiToFrontend(apiResponse);
      }
      
      setCurrentProject(savedProject);
      setIsDirty(false);
      setIsSaved(true);
      setValidationErrors([]);
      setSnackbar({
        open: true,
        message: 'Project saved successfully',
        severity: 'success',
      });
      
      // Clear saved indicator after 3 seconds
      setTimeout(() => setIsSaved(false), 3000);
    } catch (error) {
      // Handle API errors - database is the single source of truth
      let errorMessage = 'Failed to save project';
      
      if (error && typeof error === 'object' && 'code' in error) {
        if (error.code === 'AUTH_REQUIRED') {
          errorMessage = 'Authentication required. Please log in.';
        } else if (error.code === 'VALIDATION_ERROR') {
          errorMessage = 'Validation failed. Please check your input.';
        } else if ('message' in error) {
          errorMessage = (error as { message: string }).message;
        }
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
      console.error('Error saving project:', error);
    } finally {
      setIsSaving(false);
    }
  }, [currentProject, convertFrontendToApi, convertApiToFrontend, navigate]);

  const handlePreview = () => {
    if (currentProject) {
      navigate(`/builder/${currentProject.projectId}/preview`);
    }
  };

  const handleBackToDashboard = () => {
    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      );
      if (!confirmed) return;
    }
    navigate('/builder');
    setCurrentProject(null);
    setIsDirty(false);
    setValidationErrors([]);
  };

  const handleUpdateProject = (updates: Partial<ProjectDefinition>) => {
    if (!currentProject) return;
    
    const updated = { ...currentProject, ...updates };
    setCurrentProject(updated);
    setIsDirty(true);
    setIsSaved(false);
  };

  const handleAddPhase = () => {
    setEditingPhase(null);
    setShowPhaseForm(true);
  };

  const handleEditPhase = (phase: PhaseDefinition) => {
    setEditingPhase(phase);
    setShowPhaseForm(true);
  };

  const handleSavePhase = (phase: PhaseDefinition) => {
    if (!currentProject) return;

    const existingIndex = currentProject.phases.findIndex(p => p.phaseId === phase.phaseId);
    let updatedPhases: PhaseDefinition[];
    
    if (existingIndex >= 0) {
      // Update existing phase
      updatedPhases = [...currentProject.phases];
      updatedPhases[existingIndex] = phase;
    } else {
      // Add new phase
      const newPhase = {
        ...phase,
        displayOrder: currentProject.phases.length,
      };
      updatedPhases = [...currentProject.phases, newPhase];
    }

    handleUpdateProject({ phases: updatedPhases });
    setShowPhaseForm(false);
    setEditingPhase(null);
  };

  const handleDeletePhase = (phaseId: number) => {
    if (!currentProject) return;

    // Prevent deleting the last phase
    if (currentProject.phases.length <= 1) {
      setSnackbar({
        open: true,
        message: 'Project must have at least one phase',
        severity: 'error',
      });
      return;
    }

    // Remove phase
    const updatedPhases = currentProject.phases.filter(p => p.phaseId !== phaseId);
    
    // Update display orders
    updatedPhases.forEach((phase, index) => {
      phase.displayOrder = index;
    });

    // Remove transitions that reference this phase
    const updatedTransitions = currentProject.transitions.filter(
      t => t.fromPhaseId !== phaseId && t.toPhaseId !== phaseId
    );

    handleUpdateProject({ 
      phases: updatedPhases,
      transitions: updatedTransitions,
    });
  };

  const handleCancelPhaseEdit = () => {
    setShowPhaseForm(false);
    setEditingPhase(null);
  };

  const handleAddTransition = (transition: TransitionDefinition) => {
    if (!currentProject) return;
    handleUpdateProject({
      transitions: [...currentProject.transitions, transition],
    });
  };

  const handleUpdateTransition = (transitionId: number, transition: TransitionDefinition) => {
    if (!currentProject) return;
    const updatedTransitions = currentProject.transitions.map(t =>
      t.transitionId === transitionId ? transition : t
    );
    handleUpdateProject({ transitions: updatedTransitions });
  };

  const handleDeleteTransition = (transitionId: number) => {
    if (!currentProject) return;
    const updatedTransitions = currentProject.transitions.filter(
      t => t.transitionId !== transitionId
    );
    handleUpdateProject({ transitions: updatedTransitions });
  };

  // Complete validation (T14.11)
  const validateProject = (project: ProjectDefinition): ValidationError[] => {
    const errors: ValidationError[] = [];

    // Project-level validation
    if (!project.projectName || project.projectName.trim() === '') {
      errors.push({ field: 'projectName', message: 'Project name is required' });
    }

    if (project.phases.length === 0) {
      errors.push({ field: 'phases', message: 'Project must have at least one phase' });
    }

    // Phase-level validation
    const phaseNames = project.phases.map(p => p.name.toLowerCase().trim());
    const duplicatePhaseNames = phaseNames.filter((name, index) => phaseNames.indexOf(name) !== index);
    if (duplicatePhaseNames.length > 0) {
      errors.push({ field: 'phases', message: 'Phase names must be unique' });
    }

    // Phase ID uniqueness
    const phaseIds = project.phases.map(p => p.phaseId);
    const duplicatePhaseIds = phaseIds.filter((id, index) => phaseIds.indexOf(id) !== index);
    if (duplicatePhaseIds.length > 0) {
      errors.push({ field: 'phases', message: 'Phase IDs must be unique' });
    }

    // Transition-level validation
    const transitionErrors = validateTransitions(project.phases, project.transitions);
    transitionErrors.forEach(error => {
      errors.push({ field: 'transitions', message: error });
    });

    // Circular dependency detection
    const cycle = detectCircularDependencies(project.phases, project.transitions);
    if (cycle) {
      const cycleNames = formatCyclePath(cycle, project.phases);
      errors.push({ 
        field: 'transitions', 
        message: `Circular dependency detected: ${cycleNames}` 
      });
    }

    return errors;
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box sx={{ p: 3, flexGrow: 1 }}>
        {isLoading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Loading project data...
            </Typography>
          </Box>
        )}
        
        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Validation Errors:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationErrors.map((error, index) => (
                <li key={index}>{error.message}</li>
              ))}
            </ul>
          </Alert>
        )}

        {currentView === 'dashboard' && (
            <ProjectBuilderDashboard
              key={`dashboard-${dashboardKey}`} // Force remount when returning to dashboard to refresh list
              onSelectProject={handleSelectProject}
              onCreateNew={handleCreateNew}
              onCreateFromExcel={() => setExcelImportWizardOpen(true)}
              onDownloadExcelTemplate={async () => {
                try {
                  await downloadBlankExcelTemplate();
                  setSnackbar({ open: true, message: 'Template downloaded', severity: 'success' });
                } catch (err) {
                  const apiErr = err as ProjectApiError;
                  setSnackbar({ open: true, message: apiErr.message || 'Download failed', severity: 'error' });
                }
              }}
              onViewProject={handleViewProject}
            />
          )}

        {currentView === 'builder' && currentProject && (
          <>
            <PhaseNavigationToolbar
              onSave={handleSave}
              onPreview={handlePreview}
              onBack={handleBackToDashboard}
              hasUnsavedChanges={isDirty}
              isSaving={isSaving}
              isSaved={isSaved}
              onSaveAsTemplate={
                associatedProjectId
                  ? () => setShowSaveTemplateDialog(true)
                  : undefined
              }
            />
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="h5" gutterBottom>
                {currentProject.projectName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                Project ID: {currentProject.projectId}
              </Typography>

              {showPhaseForm ? (
                <PhaseConfigurationPanel
                  phase={editingPhase || undefined}
                  onSave={handleSavePhase}
                  onCancel={handleCancelPhaseEdit}
                  existingPhaseNames={currentProject.phases.map(p => p.name)}
                />
              ) : (
                <>
                  <PhaseList
                    phases={currentProject.phases}
                    onEdit={handleEditPhase}
                    onDelete={handleDeletePhase}
                    onAdd={handleAddPhase}
                  />
                  
                  <Box sx={{ mt: 4 }}>
                    <TransitionLogicEditor
                      phases={currentProject.phases}
                      transitions={currentProject.transitions}
                      onAddTransition={handleAddTransition}
                      onUpdateTransition={handleUpdateTransition}
                      onDeleteTransition={handleDeleteTransition}
                    />
                  </Box>
                  
                  {/* Show Project User Management if we have a project ID (from URL or associated project) */}
                  {((projectId && !isNaN(parseInt(projectId, 10)) && parseInt(projectId, 10) > 0) || associatedProjectId) && (
                    <Box sx={{ mt: 4 }}>
                      <ProjectUserManagement 
                        projectId={associatedProjectId || (projectId ? parseInt(projectId, 10) : 0)} 
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          </>
        )}

        {currentView === 'preview' && currentProject && (
          <ProjectDefinitionViewer
            project={currentProject}
            onExport={() => {
              setSnackbar({
                open: true,
                message: 'Project exported successfully',
                severity: 'success',
              });
            }}
            onClose={() => navigate(`/builder/${currentProject.projectId}`)}
          />
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <CreateProjectDialog
        open={showCreateProjectDialog}
        onClose={() => setShowCreateProjectDialog(false)}
        onCreate={handleCreateProject}
      />

      <ExcelImportWizard
        open={excelImportWizardOpen}
        onClose={() => setExcelImportWizardOpen(false)}
        mode="create"
        onSuccess={(projectId) => {
          setExcelImportWizardOpen(false);
          navigate(`/planning?projectId=${projectId}`);
        }}
      />

      {associatedProjectId && (
        <SaveTemplateDialog
          open={showSaveTemplateDialog}
          onClose={() => setShowSaveTemplateDialog(false)}
          projectId={associatedProjectId}
          onSuccess={(_templateId) => {
            setSnackbar({
              open: true,
              message: 'Template created successfully',
              severity: 'success',
            });
          }}
        />
      )}
    </Box>
  );
};

export default ProjectBuilderPage;

